import * as React from "react"
import { Head, Link } from "@inertiajs/react"
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  Info,
  Building2,
  MapPin,
  Clock,
  Mail,
  User,
  Users,
  Search
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

type TreeNode = {
  id: number
  name: string
  job_title: string
  department: string
  email: string
  work_location: string | null
  company: string | null
  work_hours: string | null
  manager_id: number | null
  manager_name: string | null
  children: TreeNode[]
}

type Props = {
  tree_data: TreeNode[]
}

function getAllNodeIds(nodes: TreeNode[]): Set<number> {
  const ids = new Set<number>()
  function traverse(node: TreeNode) {
    if (node.children && node.children.length > 0) {
      ids.add(node.id)
      node.children.forEach(traverse)
    }
  }
  nodes.forEach(traverse)
  return ids
}

function getAncestorIdsToExpand(nodes: TreeNode[], query: string): Set<number> {
  const ids = new Set<number>()
  if (!query) return ids

  function traverse(node: TreeNode, path: number[]): boolean {
    const isMatch = node.name.toLowerCase().includes(query.toLowerCase())
    
    let childHasMatch = false
    const currentPath = [...path, node.id]
    for (const child of node.children) {
      if (traverse(child, currentPath)) {
        childHasMatch = true
      }
    }
    
    if (isMatch || childHasMatch) {
      path.forEach(id => ids.add(id))
      return true
    }
    return false
  }

  nodes.forEach(node => traverse(node, []))
  return ids
}

export default function AdminOrgChartTree({ tree_data }: Props) {
  const [searchQuery, setSearchQuery] = React.useState("")
  
  const allNodeIds = React.useMemo(() => getAllNodeIds(tree_data), [tree_data])
  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(() => getAllNodeIds(tree_data))
  const [selectedEmployee, setSelectedEmployee] = React.useState<TreeNode | null>(null)

  // Construct flat map of employees for fast details lookup
  const flatEmployeesMap = React.useMemo(() => {
    const map = new Map<number, TreeNode>()
    function traverse(node: TreeNode) {
      map.set(node.id, node)
      node.children.forEach(traverse)
    }
    tree_data.forEach(traverse)
    return map
  }, [tree_data])

  // Automatically expand matches when search query changes
  React.useEffect(() => {
    if (searchQuery) {
      const matchingAncestors = getAncestorIdsToExpand(tree_data, searchQuery)
      setExpandedIds((prev) => {
        const next = new Set(prev)
        matchingAncestors.forEach(id => next.add(id))
        return next
      })

      // Find first match and scroll to it
      let firstMatch: TreeNode | null = null
      function findFirstMatch(nodes: TreeNode[]): boolean {
        for (const node of nodes) {
          if (node.name.toLowerCase().includes(searchQuery.toLowerCase())) {
            firstMatch = node
            return true
          }
          if (node.children && findFirstMatch(node.children)) {
            return true
          }
        }
        return false
      }

      findFirstMatch(tree_data)

      if (firstMatch) {
        const targetId = (firstMatch as TreeNode).id
        setTimeout(() => {
          const el = document.getElementById(`org-card-${targetId}`)
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }, 100)
      }
    }
  }, [searchQuery, tree_data])

  function handleToggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function handleExpandAll() {
    setExpandedIds(allNodeIds)
  }

  function handleCollapseAll() {
    setExpandedIds(new Set())
  }

  function handleJumpToEmployee(id: number) {
    const emp = flatEmployeesMap.get(id)
    if (!emp) return

    // Expand path leading to this employee
    const ancestors = getAncestorIdsToExpand(tree_data, emp.name)
    setExpandedIds((prev) => {
      const next = new Set(prev)
      ancestors.forEach((aid) => next.add(aid))
      return next
    })

    setSelectedEmployee(emp)

    // Scroll to the card
    setTimeout(() => {
      const el = document.getElementById(`org-card-${id}`)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }, 120)
  }

  return (
    <>
      <Head title="Bagan Organisasi">
        <meta name="description" content="Visualisasi bagan struktur organisasi." />
        <meta property="og:title" content="Bagan Organisasi" />
        <meta property="og:description" content="Visualisasi bagan struktur organisasi." />
      </Head>

      <AppShell>
        <PageHeader
          title="Bagan Organisasi"
          description="Struktur hierarki pelaporan pegawai."
          actions={
            <Button asChild variant="secondary" size="sm">
              <Link href="/admin/org-chart" className="no-underline">
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                Kembali ke Daftar
              </Link>
            </Button>
          }
        />

        {/* Search & Actions Bar */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative w-full max-w-md">
            <Input
              type="search"
              placeholder="Cari nama pegawai..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4"
            />
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted pointer-events-none" />
          </div>

          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleExpandAll}>
              Expand Semua
            </Button>
            <Button variant="secondary" size="sm" onClick={handleCollapseAll}>
              Collapse Semua
            </Button>
          </div>
        </div>

        {/* Tree Container */}
        {tree_data.length === 0 ? (
          <div className="mt-6 rounded-md border border-hairline bg-page p-8 text-center text-ink-muted">
            Tidak ada data pegawai.
          </div>
        ) : (
          <div className="mt-8 max-w-3xl mx-auto pb-12">
            {tree_data.map((root) => (
              <TreeNodeItem
                key={root.id}
                node={root}
                searchQuery={searchQuery}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                onSelectEmployee={setSelectedEmployee}
              />
            ))}
          </div>
        )}

      </AppShell>

      {/* Profile Detail Sheet */}
      <Dialog open={selectedEmployee !== null} onOpenChange={(open) => !open && setSelectedEmployee(null)}>
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>Detail Profil Pegawai</DialogTitle>
            </DialogHeader>
            {selectedEmployee && (
              <div className="mt-6 space-y-6">
                {/* Header profile section */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
                  <InitialsBadge name={selectedEmployee.name} size="lg" />
                  <div className="space-y-1">
                    <h3>{selectedEmployee.name}</h3>
                    <p className="text-sm text-ink-body font-medium">{selectedEmployee.job_title}</p>
                    <p className="text-xs text-ink-muted">{selectedEmployee.department}</p>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-hairline pt-4">
                  <div className="space-y-1">
                    <span className="text-xs text-ink-muted flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5" /> Email
                    </span>
                    <a
                      href={`mailto:${selectedEmployee.email}`}
                      className="text-sm font-medium text-accent-display hover:underline break-all"
                    >
                      {selectedEmployee.email}
                    </a>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs text-ink-muted flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> Lokasi Kerja
                    </span>
                    <p className="text-sm font-medium text-ink-display">
                      {selectedEmployee.work_location || "—"}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs text-ink-muted flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5" /> Perusahaan
                    </span>
                    <p className="text-sm font-medium text-ink-display">
                      {selectedEmployee.company || "—"}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs text-ink-muted flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> Jam Kerja
                    </span>
                    <p className="text-sm font-medium text-ink-display">
                      {selectedEmployee.work_hours || "—"}
                    </p>
                  </div>
                </div>

                {/* Relationship Links */}
                {(selectedEmployee.manager_id || (selectedEmployee.children && selectedEmployee.children.length > 0)) && (
                  <div className="border-t border-hairline pt-4 space-y-4">
                    <h3 className="text-sm font-semibold text-ink-display">Hubungan Organisasi</h3>
                    
                    {selectedEmployee.manager_id && (
                      <div className="space-y-1.5">
                        <span className="text-xs text-ink-muted flex items-center gap-1">
                          <User className="h-3 w-3" /> Atasan Langsung
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="w-full justify-start gap-2 text-left h-auto py-2 px-3"
                          onClick={() => handleJumpToEmployee(selectedEmployee.manager_id!)}
                        >
                          <ArrowUp className="h-3.5 w-3.5 text-ink-muted shrink-0" />
                          <div className="truncate">
                            <span className="font-medium text-ink-display">{selectedEmployee.manager_name}</span>
                          </div>
                        </Button>
                      </div>
                    )}

                    {selectedEmployee.children && selectedEmployee.children.length > 0 && (
                      <div className="space-y-1.5">
                        <span className="text-xs text-ink-muted flex items-center gap-1">
                          <Users className="h-3 w-3" /> Bawahan Langsung ({selectedEmployee.children.length})
                        </span>
                        <div className="flex flex-col gap-2">
                          {selectedEmployee.children.map((child) => (
                            <Button
                              key={child.id}
                              variant="secondary"
                              size="sm"
                              className="w-full justify-start gap-2 text-left h-auto py-2 px-3"
                              onClick={() => handleJumpToEmployee(child.id)}
                            >
                              <ArrowDown className="h-3.5 w-3.5 text-ink-muted shrink-0" />
                              <div className="truncate">
                                <span className="font-medium text-ink-display">{child.name}</span>
                                <span className="text-xs text-ink-muted ml-1.5">({child.job_title})</span>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    )
  }

interface TreeNodeItemProps {
  node: TreeNode
  searchQuery: string
  expandedIds: Set<number>
  onToggleExpand: (id: number) => void
  onSelectEmployee: (employee: TreeNode) => void
}

function TreeNodeItem({
  node,
  searchQuery,
  expandedIds,
  onToggleExpand,
  onSelectEmployee,
}: TreeNodeItemProps) {
  const isExpanded = expandedIds.has(node.id)
  const hasChildren = node.children && node.children.length > 0

  const isMatch = searchQuery
    ? node.name.toLowerCase().includes(searchQuery.toLowerCase())
    : false

  return (
    <div className="flex flex-col gap-2">
      {/* Employee Card */}
      <div
        id={`org-card-${node.id}`}
        className={cn(
          "flex items-center justify-between p-3 rounded-md border bg-surface transition-all cursor-pointer",
          isMatch
            ? "border-accent bg-accent-faded/50 shadow-sm ring-2 ring-accent"
            : "border-hairline hover:border-accent hover:bg-surface/60"
        )}
        onClick={() => onSelectEmployee(node)}
      >
        <div className="flex items-center gap-3">
          <InitialsBadge name={node.name} size="sm" />
          <div>
            <div className="font-medium text-ink-display leading-tight">{node.name}</div>
            <div className="text-xs text-ink-muted leading-tight mt-1">
              {node.job_title} &bull; {node.department}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onSelectEmployee(node)}
            title="Info Detail"
          >
            <Info className="h-4 w-4 text-ink-muted hover:text-ink-body" />
          </Button>

          {hasChildren && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleExpand(node.id)}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-ink-muted hover:text-ink-body" />
              ) : (
                <ChevronRight className="h-4 w-4 text-ink-muted hover:text-ink-body" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Children Container */}
      {hasChildren && isExpanded && (
        <div className="relative pl-4 ml-4 sm:pl-6 sm:ml-5 flex flex-col gap-3 mt-2">
          {/* Vertical connecting line */}
          <div className="absolute left-0 top-0 bottom-3 w-px bg-hairline" />
          
          {node.children.map((child) => (
            <div key={child.id} className="relative">
              {/* Horizontal line stub connecting the line to the child */}
              <div className="absolute -left-4 sm:-left-6 top-5 w-4 sm:w-6 h-px bg-hairline" />
              
              <TreeNodeItem
                node={child}
                searchQuery={searchQuery}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                onSelectEmployee={onSelectEmployee}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function InitialsBadge({ name, size = "sm" }: { name: string; size?: "sm" | "md" | "lg" }) {
  const initials = React.useMemo(() => {
    if (!name) return ""
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }, [name])

  const sizeClass = {
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-16 w-16 text-xl font-semibold"
  }[size]

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground select-none font-semibold",
        sizeClass
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  )
}
